import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import Input from 'components/Form/Input'
import ModalContainer from 'components/ModalContainer'
import Form from 'formsy-react'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useState } from 'react'
import { useStyletron } from 'styletron-react'

// Used only by the Order Feedback rating detail (RatingDetail). RecoveryAI's
// CaseDetail no longer uses it: there Mark as resolved resolves directly and
// Send message resolves via SendMessageModal.
const t = buildTranslate('components.resolve_with_reply_modal')
const validationT = buildTranslate('validations')

// Character cap on the customer-facing reply. Matches the BE bound —
// `optional :reply_message, type: String, max_length: OPERATOR_NOTE_MAX_LENGTH`
// (2_000) on both resolve endpoints (nexus PR #1404). Generous compared with the
// grant modal's 240 (that one becomes a push body, this one an email body), so
// the cap is a fat-finger/abuse guard rather than a display ceiling. Keep the
// two in lockstep if the BE bound changes.
const REPLY_MAX_LENGTH = 2000

// Textarea height, per the design — tall enough for a short paragraph without
// pushing the dialog's actions below the fold.
const REPLY_ROWS = 5

// The resolve body MINUS the record id, which each caller adds (a conversation
// id on the RAI case detail, a rating id on the Reports rating detail). Always
// 'resolved_without_reward': this modal is the dismiss path — resolving always
// happens, and the reply is the only optional part.
export type ResolveWithReplyPayload = {
  resolution_type: 'resolved_without_reward'
  reply_message?: string
}

type Props = {
  isOpen: boolean
  // The LOGGED-IN OPERATOR's email (not the customer's) — replies to the sent
  // message land there. Null when the current merchant user isn't in the store
  // yet, which hides the footer rather than rendering a half-empty sentence.
  operatorEmail: string | null
  // The caller's mutation state. onConfirm resolves on success and rejects on
  // failure; this component closes itself on the former and leaves the caller's
  // isError to render the banner on the latter.
  onConfirm: (payload: ResolveWithReplyPayload) => Promise<unknown>
  isSubmitting: boolean
  isError: boolean
  onResetError: () => void
  // Surface-specific failure copy ("resolving this case" vs "this feedback"), so
  // the shared component never hardcodes one caller's wording.
  errorMessage: string
  onClose: () => void
}

// Operator action: resolve the record, optionally sending the customer a written
// reply in the same request. PRESENTATIONAL — the write permission check lives
// in the calling scene. Mirrors components/GrantRewardModal's
// established behaviours (close-guard while submitting, full reset +
// onResetError on close, inline error banner) and reuses the shared
// ModalContainer (Base Web Modal — owns focus-trap/ESC/aria).
const ResolveWithReplyModal = ({
  isOpen,
  operatorEmail,
  onConfirm,
  isSubmitting,
  isError,
  onResetError,
  errorMessage,
  onClose,
}: Props) => {
  const [css] = useStyletron()
  const [reply, setReply] = useState('')
  // Formsy's verdict on the reply field's maxLength rule, which gates the
  // proceed button the same way the grant modal's message field does.
  const [isFormValid, setIsFormValid] = useState(true)
  // components/Form/Input is UNCONTROLLED (it renders `defaultValue`), so
  // clearing `reply` alone leaves the typed text in the DOM of a field that is
  // still on screen — which is every reset that runs while the dialog is
  // visible (Cancel, and the confirm path's .then(handleClose), both of which
  // reset before Base Web has animated the dialog out). Bumping this key
  // remounts the field, which is what actually blanks the textarea.
  const [resetCount, setResetCount] = useState(0)

  // The reply is optional, so the only things that can block the operator are an
  // over-length reply and a resolve already in flight (a second click would fire
  // a duplicate write).
  const canProceed = isFormValid && !isSubmitting

  // The drafted reply and its validation verdict, back to their initial values.
  // Shared by the Cancel/confirm path (handleClose) and the isOpen-driven reset
  // below, so the two can't drift.
  const resetDraft = useCallback(() => {
    setReply('')
    setIsFormValid(true)
    setResetCount(count => count + 1)
  }, [])

  // Reset on the CLOSED edge, not only in handleClose: the calling scenes close
  // this modal directly (setIsResolveModalOpen(false) in their record-change
  // effect) when the viewed record changes, which never runs handleClose. Base
  // Web's Modal does unmount its children once fully closed — so the textarea
  // itself goes away — but THIS component stays mounted (the scene renders it
  // for the whole life of an open record), so `reply` survives that unmount and
  // the remounted field would take its defaultValue from the previous record's
  // draft.
  //
  // Deliberately NOT calling onResetError() here: both scenes already reset
  // their mutation when the record changes, and handleClose resets it on the
  // Cancel/confirm path — calling it again on every close would be redundant
  // work on a shared mutation instance.
  useEffect(() => {
    if (isOpen) return
    resetDraft()
  }, [isOpen, resetDraft])

  const handleClose = () => {
    // Ignore close requests (Cancel / backdrop / ESC) while the resolve is in
    // flight — the POST can't be aborted client-side and would resolve and
    // re-invoke this via handleConfirm's .then, double-firing onResetError()/
    // onClose(). Mirrors GrantRewardModal / RestrictCustomerModal.
    if (isSubmitting) return
    resetDraft()
    // Clear any failed-resolve error so a stale banner doesn't show on reopen —
    // this modal's own state outlives Base Web's unmount of the dialog contents.
    onResetError()
    onClose()
  }

  const handleConfirm = () => {
    if (!canProceed) return
    const trimmed = reply.trim()
    onConfirm({
      resolution_type: 'resolved_without_reward',
      // Only sent when the operator actually typed something — an empty reply
      // must not reach the customer as a blank email.
      ...(trimmed ? { reply_message: trimmed } : {}),
    })
      .then(handleClose)
      // The failure is surfaced inline via isError below; swallow the rejection
      // so it isn't an unhandled promise.
      .catch(() => {})
  }

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={handleClose}
      title={t('title')}
      proceedText={t('proceed')}
      onProceed={handleConfirm}
      canProceed={canProceed}
      isLoading={isSubmitting}
    >
      <Form
        className="mt-m"
        onValid={() => setIsFormValid(true)}
        onInvalid={() => setIsFormValid(false)}
      >
        <Input
          key={resetCount}
          name="reply"
          componentClass="textarea"
          label={t('reply_label')}
          inputProps={{ rows: REPLY_ROWS }}
          value={reply}
          onChange={event => setReply(event.currentTarget.value)}
          className="mb-xs"
          counterStyle="bare"
          validations={{ maxLength: REPLY_MAX_LENGTH }}
          validationErrors={{
            maxLength: validationT('max_length', {
              maxLength: REPLY_MAX_LENGTH,
            }),
          }}
        />
      </Form>

      {/* Omitted when the operator's address is unknown — the sentence has no
          meaning without it. */}
      {operatorEmail && (
        <Text.BodyText5 color="grey70">
          {t('reply_to_footer', { email: operatorEmail })}
        </Text.BodyText5>
      )}

      {isError && (
        <div className={css({ marginTop: '12px' })}>
          <Notification
            kind="negative"
            size="full"
            closeable
            onClose={onResetError}
          >
            {errorMessage}
          </Notification>
        </div>
      )}
    </ModalContainer>
  )
}

export default ResolveWithReplyModal
